import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashLayout from '../../layouts/dashboard/dashLayout';
import { useAppSelector } from '../../stores';
import { listFlags, AndonFlag, getAndonFlagId } from '../../services/api/andonService';
import './andon.css';

function statusBadgeClass(status: string): string {
    const key = (status || 'pending').toLowerCase();
    const known = ['pending', 'notified', 'acknowledged', 'resolved', 'dismissed'];
    return known.includes(key) ? `andon-badge andon-badge--${key}` : 'andon-badge andon-badge--pending';
}

const AndonHub: React.FC = () => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);

    const [flags, setFlags] = useState<AndonFlag[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!users.access_token) return;

        const load = async () => {
            setLoading(true);
            try {
                const data = await listFlags(users.access_token);
                setFlags(data);
            } catch {
                toast.error(t('andon.hub.loadError', 'Failed to load Andon flags'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [users.access_token, t]);

    const formatDate = (iso: string) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    return (
        <DashLayout>
            <div className="andon-page andon-page--hub">
                <h1 className="andon-page__title">
                    {t('andon.hub.title', 'Andon Flags')}
                </h1>

                {loading ? (
                    <p className="andon-page__muted">{t('andon.hub.loading', 'Loading...')}</p>
                ) : flags.length === 0 ? (
                    <p className="andon-page__muted">
                        {t('andon.hub.empty', 'No flags have been raised yet.')}
                    </p>
                ) : (
                    <div className="andon-table-wrap">
                        <table className="andon-table">
                            <thead>
                                <tr>
                                    <th>{t('andon.hub.colOrigin', 'Origin')}</th>
                                    <th>{t('andon.hub.colUser', 'User')}</th>
                                    <th>{t('andon.hub.colMessage', 'Message')}</th>
                                    <th>{t('andon.hub.colStatus', 'Status')}</th>
                                    <th>{t('andon.hub.colDate', 'Date')}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {flags.map((flag) => {
                                    const flagId = getAndonFlagId(flag);
                                    return (
                                        <tr key={flagId}>
                                            <td>{flag.hostApp}</td>
                                            <td>{flag.userName || flag.userEmail}</td>
                                            <td className="andon-table__msg" title={flag.userMessage}>
                                                {flag.userMessage}
                                            </td>
                                            <td>
                                                <span className={statusBadgeClass(flag.status)}>
                                                    {t(`andon.hub.status.${flag.status}`, flag.status)}
                                                </span>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(flag.createdAt || flag.created_at || '')}
                                            </td>
                                            <td>
                                                <Link
                                                    to={`/dashboard/andon/${flagId}`}
                                                    className="andon-table__link"
                                                >
                                                    {t('andon.hub.view', 'View')}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </DashLayout>
    );
};

export default AndonHub;
